// 全局词汇检查器管理器，防止重复初始化
import { vocabularyLevelChecker } from './compatibility-layer';

class VocabularyLevelManager {
  private isInitializing = false;
  private initializationPromise: Promise<void> | null = null;
  private currentDictionaryType: string | null = null;
  private currentDictionaryFile: string | null = null;
  private lastLogTime = 0;
  private logThrottleMs = 5000; // Only log "already initialized" every 5 seconds

  /**
   * 确保词汇检查器只初始化一次
   * @param dictionaryType 词库类型
   * @param dictionaryFile 具体的词典文件名（可选）
   * @returns Promise<void>
   */
  async ensureInitialized(
    dictionaryType: string = 'TOEFL',
    dictionaryFile?: string
  ): Promise<void> {
    // 标准化dictionaryFile参数，避免undefined和null的差异
    const normalizedDictionaryFile = dictionaryFile || null;
    
    // 如果已经用相同的词库类型和文件初始化过，直接返回
    if (vocabularyLevelChecker.isInitialized() && 
        this.currentDictionaryType === dictionaryType &&
        this.currentDictionaryFile === normalizedDictionaryFile) {
      return;
    }

    // 如果正在初始化，等待当前初始化完成
    if (this.isInitializing && this.initializationPromise) {
      return this.initializationPromise;
    }

    // 如果需要切换词库类型或文件，或首次初始化
    this.isInitializing = true;
    this.currentDictionaryType = dictionaryType;
    this.currentDictionaryFile = normalizedDictionaryFile;

    this.initializationPromise = this.performInitialization(dictionaryType, dictionaryFile);
    
    try {
      await this.initializationPromise;
    } finally {
      this.isInitializing = false;
    }
  }

  private async performInitialization(
    dictionaryType: string,
    dictionaryFile?: string
  ): Promise<void> {
    try {
      await vocabularyLevelChecker.initialize(dictionaryType, dictionaryFile);
      this.lastLogTime = Date.now(); // Update last log time after successful initialization
    } catch (error) {
      console.error('VocabularyLevelManager: Failed to initialize:', error);
      // 重置状态以允许重试
      this.currentDictionaryType = null;
      this.currentDictionaryFile = null;
      this.initializationPromise = null;
      throw error;
    }
  }

  /**
   * 获取当前初始化状态
   */
  isInitialized(): boolean {
    return vocabularyLevelChecker.isInitialized();
  }

  /**
   * 获取当前词库类型
   */
  getCurrentDictionaryType(): string | null {
    return this.currentDictionaryType;
  }

  /**
   * 获取当前词典文件
   */
  getCurrentDictionaryFile(): string | null {
    return this.currentDictionaryFile;
  }

  /**
   * 强制重新初始化（用于调试或重置）
   */
  async forceReinitialize(
    dictionaryType: string = 'TOEFL',
    dictionaryFile?: string
  ): Promise<void> {
    console.log(`🔄 Force reinitializing vocabulary manager`);
    
    // 清理管理器状态
    this.currentDictionaryType = null;
    this.currentDictionaryFile = null;
    this.isInitializing = false;
    this.initializationPromise = null;
    
    // 强制清理词汇检查器数据
    vocabularyLevelChecker.cleanup();
    
    // 重新初始化
    await this.ensureInitialized(dictionaryType, dictionaryFile);
  }
}

// 导出单例实例
export const vocabularyLevelManager = new VocabularyLevelManager();
